import type {
  CellClassFunc,
  CellClassParams,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  HeaderClass,
  ICellRendererParams,
  IHeaderParams,
  NestedFieldPaths,
} from 'ag-grid-community';
import { compact, isEmpty, isNil, mapValues, sum } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import type { CustomMetadata, OptionData, Portfolio } from 'venn-api';
import {
  createChangesKey,
  createPCBlockColumnKey,
  createPCBlockRowKey,
  type GridStyle,
  isChangesKey,
  isPortfolioComparisonColumnGroupKey,
  type PCBlockCustomMetricGrid,
  type PortfolioComparisonColumnGroupKey,
  type StudioRequestSubject,
  type SubjectKey,
  subjectToKeyString,
} from 'venn-state';
import { GetColor, getItemColor, type Theme } from 'venn-ui-kit';
import { assert, assertNotNil, type CustomizableMetric, isOptionNode, templateStringSplit } from 'venn-utils';
import { convertRequestSubjectToItemType } from '../../analysis/compare/compareUtils';
import { formatExportableSubjectWithOptionalFee } from '../../legend';
import { classToArr, lazyMergeClasses } from '../../utils/ag-grid/styling';
import { measureHeader } from '../../utils/grids';
import { BasicHeaderRenderer } from '../components/grid/renderers/BasicHeaderRenderer';
import type { HeaderCellRendererProps } from '../components/grid/renderers/HeaderCellRenderer';
import HeaderCellRenderer from '../components/grid/renderers/HeaderCellRenderer';
import { BOLD_CLASS, DARK_COLUMN_CLASS } from '../customAnalysisContants';
import { baseHeaderClassFn } from './columnUtils';
import { getDefaultCellClass } from './gridStyling';
import { type Graph, type Node, runDfs, toKey } from './parsers';
import type { GetRequestSubjectFee } from '../../blocks/utils/hooks/use-advisory-fee';

export const MAX_CUSTOM_FIELD_CHARS = 1000;

type Allocation = {
  allocation: number;
  weight: number;
};

type Yield = {
  percentageYield: number | undefined;
  income: number | undefined;
};

/** The row data for a single subject (or column group), as opposed to {@link AllocationRowData} which contains all data for an entire row. */
export type SingleSubjectRowChunk = {
  /**
   * Label to be displayed for the row.
   */
  label: string;
  /**
   * If rows represents an option, this will contain additional option data
   */
  optionData?: OptionData;
  /**
   * Path is used by AG Grid to find data and by us to merge data between different portfolios.
   * We need to use names, not IDs, because IDs are not the same between different portfolios.
   * We should not persist the namePath between sessions, because names can change.
   * Indexes are added to path elements to make sure the path is unique, even if a portfolio contains duplicate names
   * at the same level.
   */
  path: string[];
  /** ID path is used for custom metrics storage. We need to use IDs, not names, because names can change and we persist the ID path between sessions. */
  rowIdPath: string[];
  /** Unfiltered path preserved across strategy level filtering to ensure stable custom field storage. */
  canonicalRowIdPath: string[];

  columnGroupKey: PortfolioComparisonColumnGroupKey;
  allocationMetricData: Allocation;
  yieldMetricData: Yield;
  gainLoss: number | undefined;
  isStrategy: boolean;
  fundId: string | undefined;
};

/** Contains all of the row chunks relevant to a single subject. */
export type SingleSubjectRowChunks = SingleSubjectRowChunk[];

type ColumnGroupRowData = {
  rowIdPath: string[];
  canonicalRowIdPath: string[];
  allocation: number;
  weight: number;
  percentageYield: number | undefined;
  income: number | undefined;
  gainLoss: number | undefined;
  [customMetricKey: string]: unknown;
};

type RowData = Record<PortfolioComparisonColumnGroupKey, ColumnGroupRowData | undefined>;
/** Contains all data relevant to a cohesive row, including all data for each column group. This represents a merged set of {@link SingleSubjectRowChunk}. */
export type AllocationRowData = Pick<SingleSubjectRowChunk, 'label' | 'path' | 'optionData' | 'fundId'> & {
  rowData: RowData;
  isStrategy: boolean;
};

const TOTAL_ROW_ID_PATH = ['venn-total-row'];
const TOTAL_ROW_KEY = createPCBlockRowKey(TOTAL_ROW_ID_PATH);

export const getStrategyYield = (
  childMetrics: { percentageYield: number | undefined; allocation: number }[],
  strategyAllocation: number,
) => {
  if (strategyAllocation === 0) {
    return undefined;
  }
  const childrenWeightedYield = childMetrics
    .filter(({ percentageYield }) => !isNil(percentageYield))
    .map(({ percentageYield, allocation }) => percentageYield! * (allocation / strategyAllocation));
  return childrenWeightedYield.length ? sum(childrenWeightedYield) : undefined;
};

export const getIncome = (percentageYield: number | undefined, allocation: number) => {
  return !isNil(percentageYield) && allocation !== 0 ? percentageYield * allocation : undefined;
};

export const getStrategyGainLoss = (children: { gainLoss: number | undefined }[]): number | undefined => {
  const values = children.map((c) => c.gainLoss).filter((v): v is number => !isNil(v));
  return values.length > 0 ? sum(values) : undefined;
};

export const makeTotalRow = (
  subjectPortfolioPairs: [StudioRequestSubject, Portfolio][],
  customMetrics: CustomizableMetric[],
  customMetricGrid: PCBlockCustomMetricGrid,
  rowData: AllocationRowData[],
  getAllocation: (portfolio?: Portfolio) => number | undefined,
): AllocationRowData[] => {
  const subjects = subjectPortfolioPairs.map(([subject, _portfolio]) => subject);
  const portfolios = subjectPortfolioPairs.map(([_subject, portfolio]) => portfolio);
  const changesKey = createChangesKey(subjects);

  const allocation1 = getAllocation(portfolios[0]) ?? 0;
  const allocation2 = getAllocation(portfolios[1]) ?? 0;
  const weight1 = allocation1 < 0 ? -1 : 1;
  const weight2 = allocation2 < 0 ? -1 : 1;

  const changesRowData = [
    changesKey,
    {
      rowIdPath: TOTAL_ROW_ID_PATH,
      canonicalRowIdPath: TOTAL_ROW_ID_PATH,
      allocation: allocation2 - allocation1,
      weight: weight2 - weight1,
      percentageYield: undefined,
      income: undefined,
      gainLoss: undefined,
      ...Object.fromEntries(
        customMetrics.map((metric) => [
          metric.key,
          customMetricGrid[TOTAL_ROW_KEY]?.[createPCBlockColumnKey(changesKey, metric.key)],
        ]),
      ),
    } satisfies ColumnGroupRowData,
  ] as const;
  const topLevelRows = rowData.filter((row) => row.path.length === 1);

  const subjectRowData = subjectPortfolioPairs.map(([subject, portfolio]) => {
    const subjectKey = subjectToKeyString(subject);
    const allocation = getAllocation(portfolio) ?? 0;
    const topLevelRowData = compact(topLevelRows.map((row) => row.rowData[subjectKey]));
    const percentageYield = getStrategyYield(topLevelRowData, allocation);
    const income = getIncome(percentageYield, allocation);
    const gainLoss = getStrategyGainLoss(topLevelRowData);
    return [
      subjectKey,
      {
        rowIdPath: TOTAL_ROW_ID_PATH,
        canonicalRowIdPath: TOTAL_ROW_ID_PATH,
        allocation,
        weight: allocation < 0 ? -1 : 1,
        percentageYield,
        income,
        gainLoss,
        ...Object.fromEntries(
          customMetrics.map((metric) => [
            metric.key,
            customMetricGrid[TOTAL_ROW_KEY]?.[createPCBlockColumnKey(subjectKey, metric.key)],
          ]),
        ),
      } satisfies ColumnGroupRowData,
    ] as const;
  });

  const rowDataEntries = [changesRowData, ...subjectRowData];

  // Need to use casting because fromEntries doesn't properly preserve the type otherwise
  const totalRowData = Object.fromEntries(rowDataEntries) as Record<
    (typeof rowDataEntries)[number][0],
    (typeof rowDataEntries)[number][1]
  >;

  return [
    {
      label: 'Total',
      rowData: totalRowData,
      path: ['Total'],
      isStrategy: false,
      fundId: undefined,
    },
  ];
};

export const buildCanonicalPathMapping = (
  portfolio: Portfolio | null | undefined,
  parentPath: string[] = [],
): Map<number, string[]> => {
  const mapping = new Map<number, string[]>();

  if (!portfolio) {
    return mapping;
  }

  const traverse = (node: Portfolio, currentPath: string[]) => {
    const nodePath = [...currentPath, node.id.toString()];
    mapping.set(node.id, nodePath);

    node.children?.forEach((child) => traverse(child, nodePath));
  };

  traverse(portfolio, parentPath);

  return mapping;
};

export const makePortfolioRows = (
  portfolio: Portfolio,
  subjectKey: SubjectKey,
  customMetadataMap: { [id: string]: CustomMetadata },
  getAllocation: (portfolio?: Portfolio) => number | undefined,
  canonicalPathMapping: Map<number, string[]>,
): SingleSubjectRowChunks => {
  const useNamesMap = {};
  return isNil(portfolio)
    ? []
    : [
        ...portfolio.children.flatMap((child) =>
          makePortfolioNodeRow(
            child,
            [],
            [],
            getAllocation(portfolio) ?? 0,
            subjectKey,
            useNamesMap,
            customMetadataMap,
            getAllocation,
            canonicalPathMapping,
          ),
        ),
      ];
};

export const makePortfolioNodeRow = (
  portfolio: Portfolio,
  parentIdPath: string[],
  parentNamePath: string[],
  total: number,
  columnGroupKey: PortfolioComparisonColumnGroupKey,
  usedNamesMap: { [name: string]: number },
  customMetadataMap: { [id: string]: CustomMetadata },
  getAllocation: (portfolio?: Portfolio) => number | undefined,
  canonicalPathMapping: Map<number, string[]>,
): SingleSubjectRowChunks => {
  const nameUseCount = usedNamesMap[portfolio.name] ?? 0;
  usedNamesMap[portfolio.name] = nameUseCount + 1;
  const rowIdPath = [...parentIdPath, portfolio.id.toString()];
  const canonicalRowIdPath = canonicalPathMapping.get(portfolio.id) ?? rowIdPath;
  const path = [...parentNamePath, `${portfolio.name}(${nameUseCount})`];
  const childrenUsedNamesMap = {};
  const allocation = getAllocation(portfolio) ?? 0;
  const weight = total !== 0 ? allocation / Math.abs(total) : 0;

  const childNodes =
    portfolio.children?.flatMap((child) =>
      makePortfolioNodeRow(
        child,
        rowIdPath,
        path,
        total,
        columnGroupKey,
        childrenUsedNamesMap,
        customMetadataMap,
        getAllocation,
        canonicalPathMapping,
      ),
    ) ?? [];
  const directDescendants = childNodes.filter((child) => child.path.length === path.length + 1);
  const percentageYield = portfolio.fund
    ? customMetadataMap[portfolio.fund.id]?.yield.percentageYield
    : getStrategyYield(
        directDescendants.map(({ yieldMetricData, allocationMetricData }) => ({
          percentageYield: yieldMetricData.percentageYield,
          allocation: allocationMetricData.allocation,
        })),
        allocation,
      );
  const income = getIncome(percentageYield, allocation);
  const lots = portfolio.lots ?? [];
  const gainLoss = portfolio.fund
    ? lots.length > 0
      ? allocation - sum(lots.map((lot) => lot.costBasis))
      : undefined
    : getStrategyGainLoss(directDescendants);

  const label =
    isOptionNode(portfolio) && portfolio.fund?.symbol ? `${portfolio.name} (${portfolio.fund.symbol})` : portfolio.name;

  return [
    {
      label,
      optionData: portfolio.fund?.optionData,
      fundId: portfolio.fund?.id,
      columnGroupKey,
      allocationMetricData: {
        allocation,
        weight,
      },
      rowIdPath,
      canonicalRowIdPath,
      path,
      yieldMetricData: {
        percentageYield,
        income,
      },
      gainLoss,
      isStrategy: !portfolio.fund && portfolio.designation !== 'CASH',
    },
    ...childNodes,
  ];
};

/**
 * This function pre-creates combined rows and ensures rows are given in the correct order
 * It does so by peeking into all paths for all subjects and creating the dependency graph
 * See https://twosigma.atlassian.net/browse/VS-1753 for the bug this method is fixing
 */
const createCombinedRows = (subjectIndexToRowChunks: SingleSubjectRowChunks[]): AllocationRowData[] => {
  const combinedRows: AllocationRowData[] = [];

  const graph: Graph = new Map();
  const topLevelPaths: Node[] = [];
  const pathToRowData = new Map<string, RowData>();
  const pathToMetadata = new Map<
    string,
    { label: string; isStrategy: boolean; optionData: OptionData | undefined; fundId: string | undefined }
  >();

  for (const subjectRowChunks of subjectIndexToRowChunks) {
    for (const subjectRowChunk of subjectRowChunks) {
      const key = toKey(subjectRowChunk.path);
      pathToMetadata.set(key, {
        label: subjectRowChunk.label,
        isStrategy: subjectRowChunk.isStrategy,
        optionData: subjectRowChunk.optionData,
        fundId: subjectRowChunk.fundId,
      });

      if (!pathToRowData.has(key)) {
        pathToRowData.set(key, {});
      }
      assertNotNil(pathToRowData.get(key))[subjectRowChunk.columnGroupKey] = {
        rowIdPath: subjectRowChunk.rowIdPath,
        canonicalRowIdPath: subjectRowChunk.canonicalRowIdPath,
        ...subjectRowChunk.allocationMetricData,
        ...subjectRowChunk.yieldMetricData,
        gainLoss: subjectRowChunk.gainLoss,
      };

      const parentPath = subjectRowChunk.path.slice(0, -1);
      if (isEmpty(parentPath)) {
        topLevelPaths.push(subjectRowChunk.path);
      } else {
        const parentKey = toKey(parentPath);
        if (!graph.has(parentKey)) {
          graph.set(parentKey, []);
        }
        graph.get(parentKey)?.push(subjectRowChunk.path);
      }
    }
  }

  runDfs(graph, topLevelPaths, (path) => {
    combinedRows.push({
      path,
      rowData: pathToRowData.get(toKey(path)) ?? {},
      ...(pathToMetadata.get(toKey(path)) ?? { label: '', isStrategy: false, fundId: undefined }),
    });
  });

  return combinedRows;
};

/** Merges rows of individual subjects into a single {@link AllocationRowData} containing row data for all columns. */
export const combinePortfolioRows = (
  subjects: StudioRequestSubject[],
  subjectIndexToRowChunks: SingleSubjectRowChunks[],
): AllocationRowData[] => {
  const combinedRows: AllocationRowData[] = createCombinedRows(subjectIndexToRowChunks);

  // Calculate changes from the first two subjects
  for (const row of combinedRows) {
    const [firstSubjectRowData, secondSubjectRowData] = subjectIndexToRowChunks.slice(0, 2).map((subjectRowChunks) => {
      const columnGroupKey = subjectRowChunks[0]?.columnGroupKey;
      return columnGroupKey ? row.rowData[columnGroupKey] : undefined;
    });

    // The row could exist due to a subject other than the first two subjects.
    if (!firstSubjectRowData && !secondSubjectRowData) {
      continue;
    }

    row.rowData[createChangesKey(subjects)] = {
      rowIdPath: combineRowIdPath(firstSubjectRowData, secondSubjectRowData),
      canonicalRowIdPath: combineCanonicalRowIdPath(firstSubjectRowData, secondSubjectRowData),
      allocation: (secondSubjectRowData?.allocation ?? 0) - (firstSubjectRowData?.allocation ?? 0),
      weight: (secondSubjectRowData?.weight ?? 0) - (firstSubjectRowData?.weight ?? 0),
      percentageYield: undefined,
      income: undefined,
      gainLoss: undefined,
    };
  }

  return combinedRows;
};

function combinePaths(
  firstSubjectRowData: ColumnGroupRowData | undefined,
  secondSubjectRowData: ColumnGroupRowData | undefined,
  pathExtractor: (data: ColumnGroupRowData | undefined) => string[] | undefined,
) {
  const firstPath = pathExtractor(firstSubjectRowData);
  const secondPath = pathExtractor(secondSubjectRowData);
  const maxLength = Math.max(firstPath?.length ?? 0, secondPath?.length ?? 0);
  return Array.from({ length: maxLength }, (_, i) => [firstPath?.[i] ?? 'nil', secondPath?.[i] ?? 'nil'].join('&'));
}

function combineRowIdPath(
  firstSubjectRowData: ColumnGroupRowData | undefined,
  secondSubjectRowData: ColumnGroupRowData | undefined,
) {
  return combinePaths(firstSubjectRowData, secondSubjectRowData, (data) => data?.rowIdPath);
}

function combineCanonicalRowIdPath(
  firstSubjectRowData: ColumnGroupRowData | undefined,
  secondSubjectRowData: ColumnGroupRowData | undefined,
) {
  return combinePaths(firstSubjectRowData, secondSubjectRowData, (data) => data?.canonicalRowIdPath);
}

export const combineRowsWithCustomMetrics = (
  rowData: AllocationRowData[],
  customMetrics: CustomizableMetric[],
  customMetricGrid: PCBlockCustomMetricGrid,
): AllocationRowData[] =>
  rowData.map((row) => {
    return {
      ...row,
      rowData: mapValues(row.rowData, (columnGroupData, columnGroupKey) => {
        assert(isPortfolioComparisonColumnGroupKey(columnGroupKey), `Invalid column group key: ${columnGroupKey}`);
        if (!columnGroupData) {
          return undefined;
        }

        const rowKey = createPCBlockRowKey(columnGroupData.canonicalRowIdPath);
        const customMetricRow = customMetricGrid[rowKey];
        if (customMetricRow) {
          customMetrics.forEach((metric) => {
            const columnKey = createPCBlockColumnKey(columnGroupKey, metric.key);
            columnGroupData[metric.key] = customMetricRow[columnKey];
          });
        }
        return columnGroupData;
      }),
    };
  });

const makeCellClass = (params: CellClassParams<AllocationRowData>) => {
  const dark =
    isChangesKey(params.colDef.cellRendererParams[PC_COLUMN_GROUP_KEY_FIELD]) ||
    params.colDef.cellRendererParams.dark === true;

  return compact([
    ...classToArr(getDefaultCellClass as CellClassFunc<AllocationRowData>, params),
    dark && DARK_COLUMN_CLASS,
    params.data?.isStrategy && BOLD_CLASS,
  ]);
};

const makeHeaderClasses = ({ dark }: { dark: boolean }) => compact([dark && DARK_COLUMN_CLASS]);

const Placeholder = styled.span`
  color: ${GetColor.GreyScale.Grey40};
  /* The placeholder looks weirder wrapped than not wrapped, although it shouldn't wrap in normal circumstances anyway. */
  white-space: nowrap;
  @media print {
    display: none;
    // Display none doesn't seem to be enough to prevent the placeholder text from affecting things like text wrapping or ag-grid measurement
    //  so we set font size to 0px to prevent the invisible text from affecting things.
    font-size: 0;
  }
`;

const isInPortfolio = (rowData: AllocationRowData | undefined, columnGroupKey: PortfolioComparisonColumnGroupKey) =>
  !isNil(rowData?.rowData[columnGroupKey]);

const PC_COLUMN_GROUP_KEY_FIELD = 'pcColumnKey';

/** A field path to provide to ag-grid for accessing data within an {@link AllocationRowData} object. */
export type PCField = `rowData.${PortfolioComparisonColumnGroupKey}.${string}` | `rowData.changes.${string}`;

/** Split a Portfolio Comparison block's ag-grid 'field' string into typed parts. */
export function parsePcField(field: PCField) {
  const [pcFieldPrefix, columnGroupKey, metricKey] = templateStringSplit(field, '.');
  assert(pcFieldPrefix === 'rowData', `Invalid PC field: ${field}`);
  assert(isPortfolioComparisonColumnGroupKey(columnGroupKey), `Invalid column group key: ${columnGroupKey}`);
  return {
    columnGroupKey,
    metricKey,
    columnKey: createPCBlockColumnKey(columnGroupKey, metricKey),
  } as const;
}

const makeChildrenColumnDefs = (
  metrics: CustomizableMetric[],
  columnGroupKey: PortfolioComparisonColumnGroupKey,
  gridStyle: GridStyle,
  customMetricSymbol: string,
  theme: Theme,
): ColDef<AllocationRowData>[] =>
  metrics.map((metric) => ({
    headerName: metric.label,
    minWidth: measureHeader(
      metric.label + (metric.analysisType === 'CUSTOM_ENTRY' ? customMetricSymbol : ''),
      theme,
      gridStyle,
    ),
    headerComponent: (params: IHeaderParams) =>
      metric.analysisType === 'CUSTOM_ENTRY' || metric.analysisType === 'CUSTOM_METADATA' ? (
        <span>
          {params.displayName}
          <sup>{customMetricSymbol}</sup>
        </span>
      ) : (
        params.displayName
      ),
    // todo: fix types VENN-27399
    headerClass: lazyMergeClasses(
      baseHeaderClassFn,
      makeHeaderClasses({ dark: isChangesKey(columnGroupKey) }),
    ) as HeaderClass<AllocationRowData>,
    cellRendererParams: {
      type: metric.dataType,
      [PC_COLUMN_GROUP_KEY_FIELD]: columnGroupKey,
      maxLength: MAX_CUSTOM_FIELD_CHARS,
      formatZeroAsUndefined: ['allocation', 'weight'].includes(metric.key),
    },
    cellClass: makeCellClass,
    autoHeight: true,
    // todo: fix types VENN-27399
    field: `rowData.${columnGroupKey}.${metric.key}` satisfies PCField as NestedFieldPaths<AllocationRowData>,
    editable: metric.analysisType === 'CUSTOM_ENTRY' ? isCustomFieldEditable : undefined,
    cellRendererSelector: metric.analysisType === 'CUSTOM_ENTRY' ? customFieldRendererSelector : undefined,
  }));

const PlaceholderCellRenderer = () => {
  return <Placeholder>Enter text</Placeholder>;
};

const placeholderCellRendererSelection = { component: PlaceholderCellRenderer };

function customFieldRendererSelector(params: ICellRendererParams<AllocationRowData>) {
  return !(isNil(params.value) || params.value.toString().trim() === '') ||
    !isInPortfolio(params.data, params.colDef?.cellRendererParams[PC_COLUMN_GROUP_KEY_FIELD])
    ? undefined
    : placeholderCellRendererSelection;
}

function isCustomFieldEditable(params: EditableCallbackParams<AllocationRowData>) {
  return isInPortfolio(params.data, params.colDef.cellRendererParams[PC_COLUMN_GROUP_KEY_FIELD]);
}

const makeGroupColumnColDef = (
  metrics: CustomizableMetric[],
  subject: StudioRequestSubject,
  gridStyle: GridStyle,
  customMetricSymbol: string,
  theme: Theme,
  getRequestSubjectFee: GetRequestSubjectFee,
): ColGroupDef<AllocationRowData> => ({
  headerName: formatExportableSubjectWithOptionalFee(subject, false, getRequestSubjectFee({ subject })),
  headerGroupComponent: HeaderCellRenderer,
  headerGroupComponentParams: {
    color: getItemColor(theme.Colors, convertRequestSubjectToItemType(subject)),
    subject,
    isCommonBenchmark: false,
  } as HeaderCellRendererProps,
  children: makeChildrenColumnDefs(metrics, subjectToKeyString(subject), gridStyle, customMetricSymbol, theme),
});

const deltaColumnHeader = 'Changes';

export const makeColumnDefs = (
  metrics: CustomizableMetric[],
  subjects: StudioRequestSubject[],
  gridStyle: GridStyle,
  customMetricSymbol: string,
  theme: Theme,
  getRequestSubjectFee: GetRequestSubjectFee,
): ColGroupDef<AllocationRowData>[] => {
  const gainLossMetric = metrics.find((metric) => metric.key === 'gainLoss');
  const standardMetrics = metrics.filter((metric) => metric.key !== 'gainLoss');
  const changesMetrics = standardMetrics.filter((metric) => !['percentageYield', 'income'].includes(metric.key));
  const changesColGroup: ColGroupDef<AllocationRowData> | undefined =
    subjects.length > 1
      ? {
          headerName: deltaColumnHeader,
          headerGroupComponent: BasicHeaderRenderer,
          headerGroupComponentParams: {
            displayName: deltaColumnHeader,
            color: theme.Colors.Black,
            style: { textAlign: 'right', width: '100%', color: theme.Colors.Black },
          },
          headerClass: lazyMergeClasses(
            baseHeaderClassFn,
            makeHeaderClasses({ dark: true }),
          ) as HeaderClass<AllocationRowData>,
          children: makeChildrenColumnDefs(
            changesMetrics,
            createChangesKey(subjects),
            gridStyle,
            customMetricSymbol,
            theme,
          ),
        }
      : undefined;

  const [firstSubjectColGroup, ...restSubjectColGroups] = subjects.map((subject) =>
    makeGroupColumnColDef(standardMetrics, subject, gridStyle, customMetricSymbol, theme, getRequestSubjectFee),
  );

  const gainLossColGroup: ColGroupDef<AllocationRowData> | undefined =
    gainLossMetric && subjects.length > 0
      ? {
          headerName: '',
          headerGroupComponent: BasicHeaderRenderer,
          headerGroupComponentParams: {
            displayName: '',
            color: theme.Colors.Black,
            style: { textAlign: 'right', width: '100%' },
          },
          headerClass: lazyMergeClasses(
            baseHeaderClassFn,
            makeHeaderClasses({ dark: true }),
          ) as HeaderClass<AllocationRowData>,
          children: [
            {
              headerName: gainLossMetric.label,
              minWidth: measureHeader(gainLossMetric.label, theme, gridStyle),
              headerClass: lazyMergeClasses(
                baseHeaderClassFn,
                makeHeaderClasses({ dark: true }),
              ) as HeaderClass<AllocationRowData>,
              cellRendererParams: {
                type: gainLossMetric.dataType,
                [PC_COLUMN_GROUP_KEY_FIELD]: subjectToKeyString(subjects[0]!),
                dark: true,
                formatZeroAsUndefined: false,
              },
              cellClass: makeCellClass,
              field:
                `rowData.${subjectToKeyString(subjects[0]!)}.gainLoss` satisfies PCField as NestedFieldPaths<AllocationRowData>,
            },
          ],
        }
      : undefined;

  return compact([firstSubjectColGroup, changesColGroup, ...restSubjectColGroups, gainLossColGroup]);
};
